import React, { memo, useCallback, useMemo, useState } from 'react'

import DayPicker, { DateUtils } from 'react-day-picker'

import { Button, ChevronLeft, ChevronRight, Icon, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func } from 'prop-types'

import useTranslate, { useLocale } from 'Pods/SearchPage/Shared/useTranslate'
import { setMinDatePublished, setMaxDatePublished } from 'Pods/SearchPage/Store/actions/datePublished'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

import { NOOP } from 'Utils/stableDefaults'

const DAY_PICKER_CLASS_NAMES = {
  container: 'date-filter__calendar',
  wrapper: 'date-filter__calendar-wrapper',
  interactionDisabled: 'date-filter__calendar--interaction-disabled',
  months: 'date-filter__months',
  month: 'date-filter__month',
  navBar: 'date-filter__nav-bar',
  navButtonPrev: 'date-filter__nav-button date-filter__nav-button--prev',
  navButtonNext: 'date-filter__nav-button date-filter__nav-button--next',
  navButtonInteractionDisabled: 'date-filter__nav-button--interaction-disabled',
  caption: 'date-filter__caption',
  weekdays: 'date-filter__weekdays',
  weekdaysRow: 'date-filter__weekdays-row',
  weekday: 'date-filter__weekday',
  body: 'date-filter__body',
  week: 'date-filter__week',
  weekNumber: 'date-filter__week-number',
  day: 'date-filter__day',
  footer: 'date-filter__footer',
  todayButton: 'date-filter__today-button',
  today: 'date-filter__day--today',
  selected: 'date-filter__day--selected',
  disabled: 'date-filter__day--disabled',
  outside: 'date-filter__day--outside',
}

// Sunday-first reference week (2023-01-01 is a Sunday), matching react-day-picker weekday indexes.
const WEEKDAY_REFERENCE_DATES = [...Array(7)].map((_, weekday) => new Date(2023, 0, 1 + weekday))
const MONTH_REFERENCE_DATES = [...Array(12)].map((_, monthIndex) => new Date(2023, monthIndex, 1))

const parseIsoDate = (value) => {
  if (!value) return undefined

  const [year, month, day] = value.split('-').map(Number)

  return new Date(year, month - 1, day)
}

// Local date parts, not toISOString: UTC conversion could shift the calendar day.
const toIsoDate = (date) => {
  const pad = (part) => String(part).padStart(2, '0')

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function CustomRange({ onCancel = NOOP }) {
  const translate = useTranslate()
  const locale = useLocale()

  const minDatePublished = useSearchPageStore(state => state.minDatePublished)
  const maxDatePublished = useSearchPageStore(state => state.maxDatePublished)

  const [draft, setDraft] = useState(() => ({
    from: parseIsoDate(minDatePublished),
    to: parseIsoDate(maxDatePublished),
  }))
  const [month, setMonth] = useState(() => parseIsoDate(minDatePublished) ?? new Date())

  const formatters = useMemo(() => ({
    monthTitle: new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }),
    month: new Intl.DateTimeFormat(locale, { month: 'long' }),
    weekdayShort: new Intl.DateTimeFormat(locale, { weekday: 'short' }),
    weekdayLong: new Intl.DateTimeFormat(locale, { weekday: 'long' }),
    day: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
  }), [locale])

  const localeUtils = useMemo(() => ({
    formatMonthTitle: (date) => formatters.monthTitle.format(date),
    formatWeekdayShort: (weekday) => formatters.weekdayShort.format(WEEKDAY_REFERENCE_DATES[weekday]),
    formatWeekdayLong: (weekday) => formatters.weekdayLong.format(WEEKDAY_REFERENCE_DATES[weekday]),
    formatDay: (date) => formatters.day.format(date),
    getFirstDayOfWeek: () => 1,
    getMonths: () => MONTH_REFERENCE_DATES.map(date => formatters.month.format(date)),
  }), [formatters])

  const disabledDays = useMemo(() => ({ after: new Date() }), [])

  const startDateLabel = translate('search_page.filters.date_published.start_date')
  const endDateLabel = translate('search_page.filters.date_published.end_date')
  const formattedFrom = draft.from && formatters.day.format(draft.from)
  const formattedTo = draft.to && formatters.day.format(draft.to)

  // Endpoint modifiers only pair up once both bounds exist; a lone `from` renders
  // as a single circled day instead of an open-ended range band. With a custom
  // classNames map, react-day-picker v7 appends modifier KEYS verbatim as day
  // classes, so the keys must be the full BEM class names.
  const modifiers = useMemo(() => ({
    [`${DAY_PICKER_CLASS_NAMES.day}--start`]: draft.from,
    [`${DAY_PICKER_CLASS_NAMES.day}--end`]: draft.to ?? draft.from,
  }), [draft])

  const selectedDays = useMemo(
    () => (draft.from && draft.to ? [draft.from, draft] : draft.from),
    [draft]
  )

  // With a custom classNames map, v7 keys the day's modifiers object by the mapped
  // class names, not by the logical `disabled` name. Outside days are rendered muted
  // (see `_date_filter.scss`) and neither hover nor selection paints them, so they must
  // not be selectable either.
  const handleDayClick = useCallback((day, dayModifiers) => {
    if (dayModifiers[DAY_PICKER_CLASS_NAMES.disabled] || dayModifiers[DAY_PICKER_CLASS_NAMES.outside]) return

    setDraft(currentDraft => DateUtils.addDayToRange(day, currentDraft))
  }, [])

  const handlePreviousMonth = useCallback(() => {
    setMonth(currentMonth => DateUtils.addMonths(currentMonth, -1))
  }, [])

  const handleNextMonth = useCallback(() => {
    setMonth(currentMonth => DateUtils.addMonths(currentMonth, 1))
  }, [])

  // With no applied range there is nothing to revert the draft to, so Cancel means "I do not want
  // a custom range": the parent restores the preset that selecting Custom replaced.
  const handleCancel = useCallback(() => {
    setDraft({ from: parseIsoDate(minDatePublished), to: parseIsoDate(maxDatePublished) })

    if (!minDatePublished || !maxDatePublished) {
      onCancel()
    }
  }, [minDatePublished, maxDatePublished, onCancel])

  const handleApply = useCallback(() => {
    // TODO(CU-86cb536re): UI-only for now. buildSearchParams.js intentionally omits
    // min/maxDatePublished until the listing endpoint supports date-range filtering.
    setMinDatePublished(toIsoDate(draft.from))
    setMaxDatePublished(toIsoDate(draft.to))
  }, [draft])

  return (
    <div className="date-filter">
      <div className="date-filter__header">
        <button
          type="button"
          className="date-filter__nav-button"
          aria-label={translate('search_page.filters.date_published.previous_month')}
          onClick={handlePreviousMonth}
        >
          <Icon svgComponent={ChevronLeft} size="small" color="dark" />
        </button>
        <span className="date-filter__month-title cgt-heading-100">
          {formatters.monthTitle.format(month)}
        </span>
        <button
          type="button"
          className="date-filter__nav-button"
          aria-label={translate('search_page.filters.date_published.next_month')}
          onClick={handleNextMonth}
        >
          <Icon svgComponent={ChevronRight} size="small" color="dark" />
        </button>
      </div>
      <div className="date-filter__inputs">
        {/* The label keeps the start/end context the visible text alone does not carry, so it must
            also repeat the selected date - a bare "Start date" would hide the value from AT. */}
        <span
          className={classnames('date-filter__input', !draft.from && 'date-filter__input--empty')}
          aria-label={formattedFrom ? `${startDateLabel}: ${formattedFrom}` : startDateLabel}
        >
          {formattedFrom || startDateLabel}
        </span>
        <span className="date-filter__inputs-separator" aria-hidden="true">-</span>
        <span
          className={classnames('date-filter__input', !draft.to && 'date-filter__input--empty')}
          aria-label={formattedTo ? `${endDateLabel}: ${formattedTo}` : endDateLabel}
        >
          {formattedTo || endDateLabel}
        </span>
      </div>
      <DayPicker
        classNames={DAY_PICKER_CLASS_NAMES}
        month={month}
        canChangeMonth={false}
        showOutsideDays
        locale={locale}
        localeUtils={localeUtils}
        selectedDays={selectedDays}
        modifiers={modifiers}
        disabledDays={disabledDays}
        onDayClick={handleDayClick}
      />
      <div className="date-filter__actions">
        <Button
          type="secondary"
          size="small"
          actionType="button"
          customClass="date-filter__action"
          onClick={handleCancel}
        >
          {translate('search_page.filters.date_published.cancel')}
        </Button>
        <Button
          type="primary"
          size="small"
          actionType="button"
          customClass="date-filter__action"
          disabled={!draft.from || !draft.to}
          onClick={handleApply}
        >
          {translate('search_page.filters.date_published.apply')}
        </Button>
      </div>
    </div>
  )
}

CustomRange.propTypes = {
  onCancel: func,
}

export default memo(CustomRange)
